// app/lib/device-context.client.tsx
// Client-side device context provider for cashier identity
// Token stored in memory (state) and persisted in localStorage for page refreshes

import { createContext, useContext, useState, useEffect, ReactNode } from "react";

interface DeviceContextType {
  deviceToken: string | null;
  cashierName: string | null;
  deviceSerial: string | null;
  isRegistered: boolean;
  isLoading: boolean;
  registerDevice: (
    deviceSerial: string,
    cashierName: string,
    deviceType?: string,
    metadata?: any,
    pin?: string
  ) => Promise<void>;
  clearDevice: () => void;
}

const DeviceContext = createContext<DeviceContextType | undefined>(undefined);

export function DeviceProvider({ children }: { children: ReactNode }) {
  const [deviceToken, setDeviceToken] = useState<string | null>(null);
  const [cashierName, setCashierName] = useState<string | null>(null);
  const [deviceSerial, setDeviceSerial] = useState<string | null>(null);
  // Learning Note: Start with false to prevent hydration mismatch
  // Server renders with isLoading=false, client starts with false then sets to true
  const [isLoading, setIsLoading] = useState(false);
  const [isClient, setIsClient] = useState(false);

  // Check if device is registered on mount (client-side only)
  useEffect(() => {
    // Skip during SSR
    if (typeof window === "undefined") {
      setIsLoading(false);
      return;
    }

    setIsClient(true);

    const checkDeviceRegistration = async () => {
      // Dynamically import to avoid SSR issues
      const { getOrCreateDeviceSerial } = await import("./device-setup.client");

      // Get device serial
      const serial = getOrCreateDeviceSerial();
      setDeviceSerial(serial);

      // Check if we have a temporary token from setup page (sessionStorage - highest priority)
      const tempToken = sessionStorage.getItem("device_token_temp");
      const tempCashier = sessionStorage.getItem("cashier_name_temp");

      if (tempToken && tempCashier) {
        // Device was just registered - store in localStorage for persistence
        setDeviceToken(tempToken);
        setCashierName(tempCashier);
        localStorage.setItem("radiomisr_device_token", tempToken);
        localStorage.setItem("radiomisr_cashier_name", tempCashier);
        sessionStorage.removeItem("device_token_temp");
        sessionStorage.removeItem("cashier_name_temp");
        setIsLoading(false);
        return;
      }

      // Check if we have a persisted token in localStorage
      const persistedToken = localStorage.getItem("radiomisr_device_token");
      const persistedCashier = localStorage.getItem("radiomisr_cashier_name");

      if (persistedToken && persistedCashier) {
        // Verify device is still registered before using persisted token
        try {
          const response = await fetch(
            `/api/auth/check-device?device_serial=${encodeURIComponent(serial)}`
          );
          const data = await response.json();

          if (data.registered && data.device) {
            // Device is still registered - use persisted token
            setDeviceToken(persistedToken);
            setCashierName(persistedCashier);
            setIsLoading(false);
            return;
          } else {
            // Device no longer registered - clear persisted data
            localStorage.removeItem("radiomisr_device_token");
            localStorage.removeItem("radiomisr_cashier_name");
          }
        } catch (error) {
          console.error("[Device Context] Failed to verify persisted token:", error);
          // On error, clear persisted data to be safe
          localStorage.removeItem("radiomisr_device_token");
          localStorage.removeItem("radiomisr_cashier_name");
        }
      }

      // Check server-side if device is registered (but no token available)
      try {
        const response = await fetch(
          `/api/auth/check-device?device_serial=${encodeURIComponent(serial)}`
        );
        const data = await response.json();

        if (data.registered && data.device) {
          // Device is registered, but we don't have a token
          // User needs to go to setup page to get a token with admin barcode
          console.log(
            "[Device Context] Device is registered but no token available - user needs to authenticate"
          );
        } else {
          // Device not registered
          console.log("[Device Context] Device is not registered");
        }
      } catch (error) {
        console.error("[Device Context] Failed to check device registration:", error);
        // On error, allow access but device won't be registered
      } finally {
        setIsLoading(false);
      }
    };

    checkDeviceRegistration();
  }, []);

  const registerDevice = async (
    serial: string,
    name: string,
    deviceType?: string,
    metadata?: any,
    pin?: string
  ) => {
    // This is handled by the setup page now
    // Keeping for backward compatibility
    throw new Error("Use setup page for device registration");
  };

  const clearDevice = () => {
    setDeviceToken(null);
    setCashierName(null);
    // Clear all device-related storage
    if (typeof window !== "undefined") {
      localStorage.removeItem("radiomisr_device_serial");
      localStorage.removeItem("radiomisr_device_token");
      localStorage.removeItem("radiomisr_cashier_name");
      sessionStorage.removeItem("device_token_temp");
      sessionStorage.removeItem("cashier_name_temp");
    }
  };

  return (
    <DeviceContext.Provider
      value={{
        deviceToken,
        cashierName,
        deviceSerial,
        isRegistered: !!deviceToken,
        isLoading,
        registerDevice,
        clearDevice,
      }}
    >
      {children}
    </DeviceContext.Provider>
  );
}

export function useDevice() {
  const context = useContext(DeviceContext);
  if (context === undefined) {
    throw new Error("useDevice must be used within DeviceProvider");
  }
  return context;
}
